export enum AuthConstants {
  LS_LOGIN_EMAIL = 'emailForSignIn',
  LS_SSO_LOGIN = 'isSSOLogin',
  LS_SIGNUP_TYPE = 'signUpType',
  LS_SSO_METHOD = 'ssoMethod',
  LS_TOKEN = 'token',
}

export enum LoginMethod {
  ANONYMOUS = 'anonymous',
  PASSWORD = 'password',
  EMAIL = 'emailLink',
  APPLE = 'apple.com',
  GOOGLE = 'google.com',
  MICROSOFT = 'microsoft.com',
  OAUTH2PROXY = 'oidc.oauth2proxy',
  NEW_USER = 'new_user',
}

/* eslint-disable @typescript-eslint/no-unused-vars */
import firebase from '@modules/firebase';
import { useEffect } from 'react';
import { withTimeout } from './promise-helpers';
import { createNewUser } from './user-helpers';
import { createChildLogger } from '@modules/logger';
import { apolloClient, gql } from '@services/apollo';
import { cookies } from './auth/cookies';
import { createUserDoc } from './user-doc-helpers';
import { authService } from './firebase-auth/service';

const logger = createChildLogger({ module: 'modules/auth-helpers' });

export type OAuthProvider = 'google.com' | 'microsoft.com' | 'apple.com' | 'oidc.oauth2proxy';

export type LoginMethodPayload = {
  methodId: LoginMethod | null;
  ssoEnforced: boolean;
  publicEmailAddress?: boolean;
};

function getOAuthProvider(
  provider: OAuthProvider
): firebase.auth.OAuthProvider | firebase.auth.GoogleAuthProvider {
  localStorage.setItem(AuthConstants.LS_SSO_METHOD, provider);
  switch (provider) {
    case 'google.com':
      return new firebase.auth.GoogleAuthProvider();
    case 'microsoft.com':
    case 'apple.com':
    case 'oidc.oauth2proxy':
      return new firebase.auth.OAuthProvider(provider);
  }
}

function getMessageFromErrorCode(err: firebase.auth.Error | undefined) {
  switch (err?.code) {
    case 'auth/invalid-email':
      return 'The email address provided is not valid';
    case 'auth/wrong-password':
      return 'Wrong password';
    case 'auth/user-not-found':
      return 'There is no Blinq account associated with this email address. Click the sign up button to create a new account instead.';
    case 'auth/network-request-failed':
      return 'We are sorry, something went wrong with the login process. Please try again in 1 to 2 minutes.';
    case 'auth/account-exists-with-different-credential':
      return `Your ${formatSSOProvider(
        localStorage.getItem(AuthConstants.LS_SSO_METHOD)
      )} has already been used to sign into an existing Blinq account. If you still want to create a new Blinq account you will need to use a different method.`;
    case 'auth/email-already-in-use':
      return 'This email address is already in use by another account, please sign in to that account.';
    case 'auth/invalid-action-code':
      return 'This login link has expired. Please click the button below to go back to the login screen.';
    case 'auth/credential-already-in-use':
      return `Your ${formatSSOProvider(
        localStorage.getItem(AuthConstants.LS_SSO_METHOD)
      )} has already been used to sign into an existing Blinq account. If you still want to create a new Blinq account you will need to use a different method.`;
    case 'auth/timeout':
      return 'The operation timed out, please try again.';
    case 'auth/weak-password':
      return 'The password is too weak. Please use at least 10 characters.';
    default:
      return 'An error occurred, please try again.';
  }
}

function isNotUserError(err: firebase.auth.Error | undefined) {
  switch (err?.code) {
    case 'auth/invalid-email':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/credential-already-in-use':
    case 'auth/weak-password':
    case 'auth/account-exists-with-different-credential':
      return false;
    case 'auth/network-request-failed':
    case 'auth/invalid-action-code':
    case 'auth/timeout':
    default:
      return true;
  }
}

function formatSSOProvider(provider: string | null) {
  switch (provider) {
    case 'google.com':
      return 'Google account';
    case 'microsoft.com':
      return 'Microsoft account';
    case 'apple.com':
      return 'Apple ID';
    default:
      return 'account';
  }
}

export function handleFirebaseAuthError(err: unknown, log = logger): firebase.auth.Error {
  const error = err as firebase.auth.Error;
  if (isNotUserError(error)) {
    log.error('Firebase Auth Error', { error });
  }
  return {
    code: error?.code || 'unknown',
    message: getMessageFromErrorCode(error),
  };
}

export function setSignUpType(anonymous: boolean): void {
  localStorage.setItem(AuthConstants.LS_SIGNUP_TYPE, anonymous ? 'true' : 'false');
}

export function getSignUpType(): boolean | undefined {
  const signUpType = localStorage.getItem(AuthConstants.LS_SIGNUP_TYPE);
  if (signUpType) {
    return signUpType === 'true';
  }
  return undefined;
}

export function clearSignUpType(): void {
  localStorage.removeItem(AuthConstants.LS_SIGNUP_TYPE);
}

/**
 * Signs the user in with a custom token as part of an IdP Initiated login. This
 * will log out the current user if already logged in.
 */
export async function loginWithCustomToken(token: string): Promise<firebase.User | null> {
  const log = logger.child({ function: 'loginWithCustomToken', method: 'token' });

  try {
    log.info('Trigger logging in with custom token');
    const auth = firebase.auth();

    if (auth.currentUser) {
      await auth.signOut();
    }

    const res = await firebase.auth().signInWithCustomToken(token);
    log.info('Successful logging in with custom token');
    return res.user;
  } catch (error) {
    throw handleFirebaseAuthError(error, log);
  }
}

/**
 * Kicks off a login with email link
 *
 * @param email string
 * @param queryParams any query params to handle in the response
 * @returns Promise<void>
 */
export async function loginWithEmailLink(
  email: string,
  queryParams: Record<string, string | undefined> = {}
): Promise<void> {
  const log = logger.child({ function: 'loginWithEmailLink', method: 'emailLink' });

  try {
    log.info('Trigger logging in with email link');
    const queryString = Object.entries(queryParams)
      .filter(([, value]) => value)
      .map(([key, value]) => key + '=' + value)
      .join('&');

    const url = queryString
      ? `${window.location.origin}/finish-login?${queryString}`
      : `${window.location.origin}/finish-login`;

    const actionCodeSettings: firebase.auth.ActionCodeSettings = {
      url,
      handleCodeInApp: true,
    };
    localStorage.setItem(AuthConstants.LS_LOGIN_EMAIL, email);
    await firebase.auth().sendSignInLinkToEmail(email, actionCodeSettings);
    log.info('Successful send loging with email link to email');
    return;
  } catch (err) {
    throw handleFirebaseAuthError(err, log);
  }
}

/**
 * Handles the result of an email link login
 * @param link Typically the `window.location.href`
 * @returns firebase.User | null
 */
export async function finishLoginWithEmailLink(link?: string): Promise<firebase.User | null> {
  const log = logger.child({ function: 'finishLoginWithEmailLink', method: 'emailLink' });

  try {
    log.info('Triggering finish login with email link');
    const emailLink = link || window.location.href;
    const email = localStorage.getItem(AuthConstants.LS_LOGIN_EMAIL);

    if (!email || !firebase.auth().isSignInWithEmailLink(emailLink)) {
      return null;
    }

    const result = await firebase.auth().signInWithEmailLink(email, window.location.href);

    if (result.user) {
      log.info('Successful finish login with email link');
      return result.user;
    }

    return null;
  } catch (err) {
    throw handleFirebaseAuthError(err, log);
  }
}

/**
 * Maps a LoginMethod to an OAuthProvider so it can be using in methods like `loginWithSocialProvider`
 * Throws an error if the LoginMethod does not map to an OAuthProvider
 *
 * @param loginMethod LoginMethod
 * @returns OAuthProvider
 */
export function mapLoginMethodToOAuthProvider(loginMethod: LoginMethod): OAuthProvider {
  switch (loginMethod) {
    case LoginMethod.GOOGLE:
      return 'google.com';
    case LoginMethod.MICROSOFT:
      return 'microsoft.com';
    case LoginMethod.APPLE:
      return 'apple.com';
    case LoginMethod.OAUTH2PROXY:
      return 'oidc.oauth2proxy';
    default:
      throw new Error('Invalid login method does not map to an OAuthProvider');
  }
}

/**
 * Creates a new login with email and password that is attached to the current user
 * @param email string
 * @param password string
 * @returns firebase.User
 */
export async function addEmailAndPasswordLoginToCurrentUser(
  email: string,
  password: string
): Promise<firebase.User | null> {
  try {
    const user = firebase.auth().currentUser;

    if (!user) {
      return null;
    }

    const credential = firebase.auth.EmailAuthProvider.credential(email, password);

    await user.linkWithCredential(credential);

    await authService.signInWithEmailAndPassword(email, password);
    const updatedUser = firebase.auth().currentUser;
    return updatedUser;
  } catch (error) {
    logger.error('Failed to create Email Auth user', { error });
    throw handleFirebaseAuthError(error);
  }
}

/**
 * Used to attach a social provider to the current identity
 *
 * @param provider OAuthProvider
 * @returns Promsie<void>
 */
export async function addSocialProviderLoginToCurrentUser(provider: OAuthProvider): Promise<void> {
  try {
    const user = firebase.auth().currentUser;

    if (!user) {
      return;
    }
    const oauthProvider = getOAuthProvider(provider);
    // Used to set login loading state immediately upon return
    localStorage.setItem(AuthConstants.LS_SSO_LOGIN, '1');

    return user.linkWithRedirect(oauthProvider);
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

async function getLoginMethodQuery(
  email: string
): Promise<{ provider?: string; ssoEnforced: boolean; publicEmailAddress: boolean }> {
  const query = gql`
    query LoginProvider($email_address: String!) {
      loginProvider(email_address: $email_address) {
        provider_id
        sso_enforced
        public_email_address
      }
    }
  `;
  const queryResult = await apolloClient.query({
    query,
    variables: {
      email_address: email,
    },
    context: {
      isUnauthenticatedRequest: true,
    },
  });

  return {
    provider: queryResult.data.loginProvider.provider_id,
    ssoEnforced: queryResult.data.loginProvider.sso_enforced,
    publicEmailAddress: queryResult.data.loginProvider.public_email_address,
  };
}

/**
 * Returns the single highest priority login method for a given email address
 * @param email string
 * @returns a LoginMethodPayload
 */
export async function getLoginMethod(email: string): Promise<LoginMethodPayload> {
  try {
    const loginMethod = await getLoginMethodQuery(email);

    let methodId: LoginMethod;
    switch (true) {
      case loginMethod.provider === LoginMethod.OAUTH2PROXY:
        methodId = LoginMethod.OAUTH2PROXY;
        break;
      case loginMethod.provider === LoginMethod.PASSWORD:
        methodId = LoginMethod.PASSWORD;
        break;
      case loginMethod.provider === LoginMethod.EMAIL:
        methodId = LoginMethod.EMAIL;
        break;
      case loginMethod.provider === LoginMethod.GOOGLE:
        methodId = LoginMethod.GOOGLE;
        break;
      case loginMethod.provider === LoginMethod.APPLE:
        methodId = LoginMethod.APPLE;
        break;
      case loginMethod.provider === LoginMethod.MICROSOFT:
        methodId = LoginMethod.MICROSOFT;
        break;
      default:
        methodId = LoginMethod.NEW_USER;
        break;
    }

    return {
      methodId,
      ssoEnforced: loginMethod.ssoEnforced,
      publicEmailAddress: loginMethod.publicEmailAddress,
    };
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * Sends a password reset email to the user
 * @param email string
 */
export async function sendResetLink(email: string): Promise<void> {
  try {
    await firebase.auth().sendPasswordResetEmail(email);
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * Chooses the best login method for the email address supplied
 *
 * @param email string
 * @returns LoginMethod
 */
export async function pickLoginMethodForEmail(email: string): Promise<LoginMethodPayload> {
  try {
    if (!email) {
      return { methodId: LoginMethod.NEW_USER, ssoEnforced: false };
    }

    return await getLoginMethod(email);
  } catch {
    return { methodId: LoginMethod.NEW_USER, ssoEnforced: false };
  }
}

/**
 *
 * @param user The firebase user object
 * @returns
 */
export function pickLoginMethodForLoggedInUser(user: firebase.User | null): {
  [key in Exclude<LoginMethod, LoginMethod.ANONYMOUS | LoginMethod.NEW_USER>]: boolean;
} {
  const providers = user?.providerData || [];

  return {
    [LoginMethod.OAUTH2PROXY]: providers.some((provider) => provider?.providerId === LoginMethod.OAUTH2PROXY),
    [LoginMethod.PASSWORD]: providers.some((provider) => provider?.providerId === LoginMethod.PASSWORD),
    [LoginMethod.EMAIL]: providers.some((provider) => provider?.providerId === LoginMethod.EMAIL),
    [LoginMethod.GOOGLE]: providers.some((provider) => provider?.providerId === LoginMethod.GOOGLE),
    [LoginMethod.APPLE]: providers.some((provider) => provider?.providerId === LoginMethod.APPLE),
    [LoginMethod.MICROSOFT]: providers.some((provider) => provider?.providerId === LoginMethod.MICROSOFT),
  };
}

/**
 * Wraps the Firebase getIdToken method while also setting the token in local storage
 *
 * @param forceRefresh always retrieve a new token
 * @returns a JWT string
 */
export async function getToken(forceRefresh?: true): Promise<string | null> {
  // this is to guard against cases where Apollo Client is used server-side
  if (typeof window === 'undefined') {
    return null;
  }

  let token = localStorage.getItem(AuthConstants.LS_TOKEN);

  if (token && !forceRefresh) {
    // Check token expiry
    const [, payloadBase64] = token.split('.');
    const payload = JSON.parse(atob(payloadBase64));

    // Subtract 60 seconds to account for clock skew
    if (payload.exp > Date.now() / 1000 - 60) {
      return token;
    }
  }

  try {
    const user = firebase.auth().currentUser;

    if (!user) {
      return null;
    }

    token = await user.getIdToken(forceRefresh);
    localStorage.setItem(AuthConstants.LS_TOKEN, token);

    return token;
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * Do not use this function, it is already mounted in UserDetails.tsx
 *
 * This function provides a way to listen to auth state changes
 * and set them in localStorage. This is useful for reducing
 * time to first paint and also for server-side requests.
 */
export function useOnIdTokenChangedObserver(loggedInCallback?: () => Promise<unknown>): void {
  useEffect(() => {
    return firebase.auth().onIdTokenChanged(async (observer) => {
      if (!observer) {
        cookies.delete('token');
        localStorage.removeItem(AuthConstants.LS_TOKEN);
        return;
      }

      const token = await observer.getIdToken();

      if (token) {
        cookies.set('token', token);
        localStorage.setItem(AuthConstants.LS_TOKEN, token);
        // If the user is not anonymous, we can call the callback
        if (isIdentityBelongToAnonUser(observer) === false) {
          const hasCallback = !!loggedInCallback;
          loggedInCallback?.();
        }
        return;
      }
    });
  }, [loggedInCallback]);
}

/**
 * This function is mainly to check if a firebase user is truly anon in our system. Since
 * if the user is signed in using the custom (i.e. through session continuity) then is
 * isAnonymous will be false for that user even though the user does not have any credential
 * linked to them
 * @param user firebase user
 * @returns
 */
export function isIdentityBelongToAnonUser(firebaseUser: firebase.User | null | undefined): boolean {
  const isFirebaseUserAnonymous = firebaseUser?.isAnonymous;

  // when the user signed in with Blinq's custom token, the user will have an empty provider data
  const isUserSignedInWithBlinqCustomToken =
    !isFirebaseUserAnonymous && firebaseUser?.providerData.length === 0;

  return isFirebaseUserAnonymous || isUserSignedInWithBlinqCustomToken;
}
